/**
 * Carry the SESSION-scoped query parameters across a rebuilt URL.
 *
 * `LessonStart` and `TourOverlay.stepUrl` both build a fresh `URLSearchParams`
 * for the screen a step lives on, because a step owns its own setup params and
 * inheriting the previous step's would leak stale state into the next screen.
 *
 * Some parameters are not the step's to discard, though:
 *
 * - `EmployeeId` / `Role` — the host app's identity contract. Dropping them put
 *   this app in a ~700-writes-per-second navigation loop: the rebuilt URL lost
 *   them, `useIdentityInUrl` (`@/session/identity-url`) put them back, which
 *   re-triggered the rebuild. A learner tapping "Exit lesson" mid-loop was
 *   navigated straight back into the tour by the in-flight sync effect.
 * - `lang` — the learner's own choice, and URL-only state. Dropping it reverted
 *   a Chinese lesson to English on the first tap.
 *
 * Matching is case-insensitive because the host may send `employeeid`, and
 * `readIdentity` accepts any casing (claim quick-kayinleong-026) — so an
 * off-case key still has to survive the rebuild.
 *
 * Call this AFTER writing the step's own params, so the result is a fixed
 * point: feeding a URL this produced back in yields the identical string, which
 * is what stops the sync effect from navigating a second time.
 */
import { EMPLOYEE_PARAM, ROLE_PARAM } from '@/session/identity'

const CARRIED = new Set([EMPLOYEE_PARAM, ROLE_PARAM, 'lang'].map((n) => n.toLowerCase()))

export function carrySessionParams(
  from: URLSearchParams | undefined,
  to: URLSearchParams,
): URLSearchParams {
  if (!from) return to
  for (const [name, value] of from) {
    if (CARRIED.has(name.toLowerCase()) && !to.has(name)) to.set(name, value)
  }
  return to
}
